import React, { FC, useEffect, useMemo } from 'react';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { HomePageRedesignFullscreenPreloader } from '~/components/atoms/FullscreenPreloader';
import { ModalContextProvider } from '~/contexts/modal/ModalContext';
import { PopoverContextProvider } from '~/contexts/popover/PopoverContext';
import { DesignSystemSvgFilters } from '~/design-system/components/SvgFilters/DesignSystemSvgFilters';
import { SnackBarContextProvider } from '~/features/snackbar/SnackbarContext';
import { setCssVariables } from '~/helpers/cssVariables';
import i18n from '~/i18n/config';
import { CheckAuthWrapper } from '~/middlewares/CheckAuthWrapper';
import ProfileStatusWrapper from '~/middlewares/ProfileStatusWrapper';
import { SubRebillWrapper } from '~/middlewares/SubRebillWrapper';
import GrammarCheckerWrapper from '~/modules/grammar-checker/GrammarCheckerWrapper';
import { HighlightContextProvider } from '~/modules/highlight/HighlightContext';
import {
  CreateCustomTheme,
  HomePageRedesignThemeProvider,
  PROJECT_THEME_CSS_VARIABLE_GROUPS,
} from '~/modules/home/HomePageRedesignThemeProvider';
import Router from '~/Router';
import GrowthBookProvider from '~/services/abTests/Provider';
import { store } from '~/store';

import SurveyManager from './components/organisms/SurveyManager/SurveyManager';

const themeMode = 'light';

type Props = {
  createCustomTheme: CreateCustomTheme;
};

const App: FC<Props> = ({ createCustomTheme }) => {
  const theme = useMemo(
    () => createCustomTheme(themeMode, { variant: 'legacy' }),
    [createCustomTheme],
  );

  useEffect(() => {
    setCssVariables(theme.palette, PROJECT_THEME_CSS_VARIABLE_GROUPS);
  }, [theme]);

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n.instance}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <DesignSystemSvgFilters />
          <Provider store={store}>
            <BrowserRouter>
              <SnackBarContextProvider>
                <PopoverContextProvider>
                  <HighlightContextProvider>
                    <CheckAuthWrapper>
                      <GrowthBookProvider>
                        <HomePageRedesignThemeProvider
                          createCustomTheme={createCustomTheme}
                        >
                          <ModalContextProvider>
                            <ProfileStatusWrapper>
                              <SubRebillWrapper>
                                <React.Suspense
                                  fallback={
                                    <HomePageRedesignFullscreenPreloader />
                                  }
                                >
                                  <GrammarCheckerWrapper>
                                    <Router />
                                  </GrammarCheckerWrapper>
                                </React.Suspense>
                              </SubRebillWrapper>
                              <SurveyManager />
                            </ProfileStatusWrapper>
                          </ModalContextProvider>
                        </HomePageRedesignThemeProvider>
                      </GrowthBookProvider>
                    </CheckAuthWrapper>
                  </HighlightContextProvider>
                </PopoverContextProvider>
              </SnackBarContextProvider>
            </BrowserRouter>
          </Provider>
        </ThemeProvider>
      </I18nextProvider>
    </HelmetProvider>
  );
};

export default App;
